import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import {
    AmaronCallStatusEnum,
    AmaronEventTypes,
    AMARON__EVENT__CALLS__REMARKS__COLUMN_ID,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/amaron/Events";
import type {OttopilotEvent} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEvent";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import {logActionTypeToDisplayName} from "~/domainModels/logs";
import {
    formatAmaronScheduledCallDateDisplay,
    resolveAmaronCallScheduledOnDisplayValue,
} from "~/domainModels/workspaceSpecific/amaron";
import type {
    ActivityTimelineBuildContext,
    EventTypeActivityCardData,
} from "~/modules/activityTimeline/types/activityTimeline.types";
import {formatActivityColumnFieldValue} from "~/modules/activityTimeline/utils/activityTimelineDisplayUtils";

export type EventActivityContextResult = {
    context: string;
    contextPrefix?: string;
    contextOldValue?: string;
    contextNewValue?: string;
};

function normalizeStatus(value: string | null | undefined): string {
    return (value ?? "").trim().toLowerCase();
}

function isCallScheduledStatus(
    statusDisplay: string | undefined,
    statusRaw: string | null | undefined,
): boolean {
    if (statusRaw !== null && statusRaw !== undefined && String(statusRaw) === String(AmaronCallStatusEnum.SCHEDULED)) {
        return true;
    }
    return normalizeStatus(statusDisplay) === "scheduled";
}

function isCallLoggedStatus(statusDisplay: string | undefined, statusRaw: string | null | undefined): boolean {
    if (statusRaw !== null && statusRaw !== undefined && String(statusRaw) === String(AmaronCallStatusEnum.LOGGED)) {
        return true;
    }
    return normalizeStatus(statusDisplay) === "logged";
}

function resolveEventTemplate(
    context: ActivityTimelineBuildContext,
    eventTemplateId: EventTypeActivityCardData["eventTemplateId"],
): OttopilotEventTemplate | undefined {
    return context.eventTemplates.find((template) => template.id === eventTemplateId);
}

function resolveEventForCard(
    card: EventTypeActivityCardData,
    context: ActivityTimelineBuildContext,
): OttopilotEvent | undefined {
    return context.eventContainer.events.find((event) => event.id === card.eventId);
}

function resolveWhatsAppTemplateContext(getAdditionalColumns: CapabilityUdoColumnResolver,
    event: OttopilotEvent | undefined,
    eventTemplate: OttopilotEventTemplate | undefined,
    context: ActivityTimelineBuildContext,
): string {
    if (eventTemplate !== undefined) {
        for (const column of getAdditionalColumns(eventTemplate)) {
            if (/template/i.test(column.displayName) !== true) {
                continue;
            }
            const raw = event?.additionalColumnValues[column.id];
            if (raw === null || raw === undefined) {
                continue;
            }
            const formatted = formatActivityColumnFieldValue(getAdditionalColumns, raw, String(column.id), {
                context: context,
                eventTemplateId: eventTemplate.id,
                users: context.users,
                userTemplates: context.userTemplates,
                organizationalUnits: context.organizationalUnits,
                organizationalUnitTemplates: context.organizationalUnitTemplates,
                platformUserDetailsByUserId: context.platformUserDetailsByUserId,
            });
            if (formatted !== "-" && formatted.trim() !== "") {
                return `Template - ${formatted}`;
            }
        }
    }

    return "Template";
}

export function resolveEventTemplateDisplayTitle(
    context: ActivityTimelineBuildContext,
    eventTemplateId: EventTypeActivityCardData["eventTemplateId"],
): string {
    const templateName = resolveEventTemplate(context, eventTemplateId)?.displayName ?? "Event";

    if (String(eventTemplateId) === String(AmaronEventTypes.Calls)) {
        return "Call";
    }

    if (/whatsapp/i.test(templateName)) {
        return "WhatsApp msg sent";
    }

    return templateName;
}

export function resolveEventTypeActivityContext(getAdditionalColumns: CapabilityUdoColumnResolver,
    card: EventTypeActivityCardData,
    context: ActivityTimelineBuildContext,
    statusDisplay: string | undefined,
): EventActivityContextResult {
    if (card.simpleText !== null && card.simpleText !== undefined && card.simpleText.trim() !== "") {
        return {context: card.simpleText};
    }

    const event = resolveEventForCard(card, context);
    const eventTemplate = resolveEventTemplate(context, card.eventTemplateId);
    const templateName = eventTemplate?.displayName ?? "Event";

    if (String(card.eventTemplateId) === String(AmaronEventTypes.Calls)) {
        if (isCallScheduledStatus(statusDisplay, card.statusRaw)) {
            const scheduledRaw =
                event !== undefined ? resolveAmaronCallScheduledOnDisplayValue(event.additionalColumnValues) : null;
            const dateDisplay = formatAmaronScheduledCallDateDisplay(scheduledRaw);
            return {
                context: dateDisplay !== "-" ? `Scheduled for ${dateDisplay}` : "Scheduled",
            };
        }

        if (isCallLoggedStatus(statusDisplay, card.statusRaw)) {
            return {context: "Call logged with disposition"};
        }
    }

    if (/whatsapp/i.test(templateName)) {
        return {context: resolveWhatsAppTemplateContext(getAdditionalColumns, event, eventTemplate, context)};
    }

    const actionLabel = logActionTypeToDisplayName(card.actionType);
    return {
        context: `${actionLabel} ${templateName}${card.eventIndex > 0 ? ` #${card.eventIndex + 1}` : ""}`,
    };
}

export function resolveEventTypeActivityRemarks(
    card: EventTypeActivityCardData,
    context: ActivityTimelineBuildContext,
): string | undefined {
    if (card.remarks !== null && card.remarks !== undefined && card.remarks.trim() !== "") {
        return card.remarks.trim();
    }

    const event = resolveEventForCard(card, context);
    if (event === undefined || context.workspaceSpecific?.tryAmaronLevelColumns !== true) {
        return undefined;
    }

    if (String(card.eventTemplateId) !== String(AmaronEventTypes.Calls)) {
        return undefined;
    }

    const raw = event.additionalColumnValues[AMARON__EVENT__CALLS__REMARKS__COLUMN_ID];
    if (typeof raw !== "string" || raw.trim() === "") {
        return undefined;
    }

    return raw.trim();
}
